import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
} from "../../services/api";
import { getUser } from "../../utils/auth";
import { FiEdit, FiEye, FiSearch, FiTrash2 } from "react-icons/fi";
import { MdOutlineToggleOff, MdOutlineToggleOn } from "react-icons/md";

import PeopleIcon from "@mui/icons-material/People";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import BadgeIcon from "@mui/icons-material/Badge";
import globalStyles, {
  COLORS,
  ROLE_COLORS,
  ROLE_TEXT_COLORS,
} from "../../styles/globalStyles";
import { getPageNumbers } from "../../helpers/Helpers";

const getId = (item) => item?._id || item?.id || item || "";
const getReportingManagerId = (user) => {
  const manager = user?.reportingManager;
  if (!manager) return "";
  return typeof manager === "object" ? getId(manager) : manager;
};
const isSalesRole = (role) =>
  role === "sales" || role === "sales-person" || role === "salesperson";

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

const s = {};

const blank = {
  name: "",
  email: "",
  mobile: "",
  role: "sales",
  region: "",
  reportingManager: "",
  password: "",
};

const UserTile = ({ value, label, icon: Icon, color, active, onClick }) => (
  <div
    style={{ ...globalStyles.tile, ...(active ? globalStyles.activeTile : {}) }}
    onClick={onClick}
  >
    <div style={globalStyles.tileIcon(color)}>
      <Icon fontSize="medium" />
    </div>

    <div style={globalStyles.tileText}>
      <div style={globalStyles.tileValue}>{value ?? 0}</div>
      <div style={globalStyles.tileLabel}>{label}</div>
    </div>
  </div>
);

export default function Users() {
  const navigate = useNavigate();
  const loggedIn = getUser();
  const isAdmin = loggedIn?.role === "admin";
  const isManager = ["admin", "senior-manager", "manager"].includes(
    loggedIn?.role,
  );
  const isManagerOnly = ["senior-manager", "manager"].includes(loggedIn?.role);

  const canCreate = isManager;
  const canDelete = isAdmin;

  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [err, setErr] = useState("");

  const [keyword, setKeyword] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [fieldErr, setFieldErr] = useState({});

  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);

  const isMobile = useIsMobile();

  const allowedRoles = useMemo(() => {
    if (isAdmin) {
      return ["admin", "senior-manager", "manager", "sales"];
    }

    return ["senior-manager", "manager", "sales"];
  }, [isAdmin]);

  const load = () => {
    setLoading(true);

    const params = {
      page,
      limit,
    };

    if (keyword) params.search = keyword;
    if (roleFilter) params.role = roleFilter;

    Promise.all([getUsers(params), getUsers({ limit: 1000 })])
      .then(([userRes, allUserRes]) => {
        const userData = userRes?.data?.data || {};
        const apiUsers = Array.isArray(userData.users) ? userData.users : [];

        setUsers(apiUsers);
        setTotal(userData.total || 0);
        setTotalPages(userData.totalPages || 1);
        setIsLastPage(Boolean(userData.isLastPage));

        if (isAdmin) {
          const allUsersData = allUserRes?.data?.data || {};
          const allApiUsers = Array.isArray(allUsersData.users)
            ? allUsersData.users
            : [];

          setAllUsers(allApiUsers);
        } else {
          setAllUsers([]);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [isAdmin, page, limit, keyword, roleFilter]);

  const getFieldError = (key) => {
    const error = fieldErr[key];

    if (Array.isArray(error)) return error[0];

    if (typeof error === "object" && error?.message) {
      return error.message;
    }

    return error || "";
  };

  const canEditUser = (user) => {
    if (isAdmin) return true;
    if (!isManager) return false;

    return getReportingManagerId(user) === getId(loggedIn);
  };

  const showReportingManagerDropdown = isAdmin;
  const showReportingManagerInfo = isManagerOnly && !isAdmin;

  const reportingManagerOptions = useMemo(() => {
    const currentEditingId = editing;

    return allUsers.filter((user) => getId(user) !== currentEditingId);
  }, [allUsers, editing]);

  const totalUsers = allUsers.length || total;

  const totalSeniorManagers = allUsers.filter(
    (user) => user.role === "senior-manager",
  ).length;

  const totalManagers = allUsers.filter(
    (user) => user.role === "manager",
  ).length;

  const totalSalespersons = allUsers.filter((user) =>
    isSalesRole(user.role),
  ).length;

  const userTiles = [
    {
      show: true,
      value: totalUsers,
      label: "Total Users",
      icon: PeopleIcon,
      color: "#22c55e",
      filter: "",
    },
    {
      show: true,
      value: totalSeniorManagers,
      label: "Total Senior Managers",
      icon: SupervisorAccountIcon,
      color: "#3bf6c4",
      filter: "senior-manager",
    },
    {
      show: true,
      value: totalManagers,
      label: "Total Managers",
      icon: SupervisorAccountIcon,
      color: "#3b82f6",
      filter: "manager",
    },
    {
      show: true,
      value: totalSalespersons,
      label: "Total Salespersons",
      icon: BadgeIcon,
      color: "#f59e0b",
      filter: "sales",
    },
  ];
  const openCreate = () => {
    setForm({
      ...blank,
      role: "sales",
      reportingManager: isAdmin ? "" : getId(loggedIn),
    });

    setEditing(null);
    setErr("");
    setModal(true);
  };

  const openEdit = (user) => {
    setForm({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      role: user.role || "sales",
      region: user.region || "",
      reportingManager: getReportingManagerId(user),
      password: "",
    });

    setEditing(getId(user));
    setErr("");
    setModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "role" ? { reportingManager: "" } : {}),
    }));
  };

  const handleRoleTileClick = (role) => {
    setRoleFilter(role);
    setPage(1);
  };

  const handleView = (user) => {
    navigate(`/user/${getId(user)}`, {
      state: { user },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setFieldErr({});

    const payload = { ...form };

    if (!payload.password) {
      delete payload.password;
    }

    if (!isAdmin) {
      payload.reportingManager = getId(loggedIn);
    }

    if (!payload.reportingManager) {
      delete payload.reportingManager;
    }

    try {
      if (editing) {
        await updateUser(editing, payload);
      } else {
        await createUser(payload);
      }

      setModal(false);
      setEditing(null);
      load();
    } catch (error) {
      const apiErrors = error.response?.data?.errors || {};
      const message = error.response?.data?.message || "Operation failed";

      if (Object.keys(apiErrors).length > 0) {
        setFieldErr(apiErrors);
        setErr("");
      } else {
        setFieldErr({});
        setErr(message);
      }
    }
  };

  const handleCertifiedToggle = async (user) => {
    const userId = getId(user);
    const nextStatus = !user.isCertified;

    try {
      await updateUser(userId, {
        isCertified: nextStatus,
      });

      setUsers((prev) =>
        prev.map((item) =>
          getId(item) === userId ? { ...item, isCertified: nextStatus } : item,
        ),
      );

      setAllUsers((prev) =>
        prev.map((item) =>
          getId(item) === userId ? { ...item, isCertified: nextStatus } : item,
        ),
      );
    } catch (error) {
      alert(error.response?.data?.message || "Certified status update failed");
    }
  };

  const handleDemoPersonToggle = async (user) => {
    const userId = getId(user);
    const nextStatus = !user.isDemoPerson;

    try {
      await updateUser(userId, {
        isDemoPerson: nextStatus,
      });

      setUsers((prev) =>
        prev.map((item) =>
          getId(item) === userId ? { ...item, isDemoPerson: nextStatus } : item,
        ),
      );

      setAllUsers((prev) =>
        prev.map((item) =>
          getId(item) === userId ? { ...item, isDemoPerson: nextStatus } : item,
        ),
      );
    } catch (error) {
      alert(
        error.response?.data?.message || "Demo person status update failed",
      );
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;

    try {
      await deleteUser(id);
      load();
    } catch (error) {
      alert(error.response?.data?.message || "Delete failed");
    }
  };

  return (
    <div
      style={{
        ...globalStyles.contentWrapper,
        ...(isMobile ? globalStyles.responsive.mobileContentWrapper : {}),
      }}
    >
      <div style={globalStyles.row}>
        <h1 style={globalStyles.h1}>Users</h1>

        {canCreate && (
          <button type="button" style={globalStyles.btn} onClick={openCreate}>
            + Add User
          </button>
        )}
      </div>

      <div
        style={{
          ...globalStyles.tilesGrid,
          ...(isMobile ? globalStyles.responsive.mobileTilesGrid : {}),
        }}
      >
        {userTiles
          .filter((tile) => tile.show)
          .map((tile) => (
            <UserTile
              key={tile.label}
              value={tile.value}
              label={tile.label}
              icon={tile.icon}
              color={tile.color}
              active={roleFilter === tile.filter}
              onClick={() => handleRoleTileClick(tile.filter)}
            />
          ))}
      </div>

      <div
        style={{
          ...globalStyles.toolbar,
          ...(isMobile ? globalStyles.responsive.mobileToolbar : {}),
        }}
      >
        <div
          style={{
            ...globalStyles.filterLeft,
            ...(isMobile ? globalStyles.responsive.mobileFilterLeft : {}),
          }}
        >
          <div
            style={{
              ...globalStyles.searchWrap,
              ...(isMobile ? globalStyles.responsive.mobileSearchWrap : {}),
            }}
          >
            <FiSearch style={globalStyles.searchIcon} />

            <input
              style={globalStyles.searchInput}
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name, email, mobile, region..."
            />
          </div>
        </div>
        <span style={globalStyles.paginatePageInfo}>
          Page {page} of {totalPages}
        </span>
      </div>

      {showReportingManagerInfo && (
        <div style={globalStyles.info}>
          You are viewing users who report directly to you.
        </div>
      )}

      {loading ? (
        <div style={globalStyles.loading}>Loading...</div>
      ) : (
        <div style={globalStyles.tableScroll}>
          <table style={globalStyles.table}>
            <thead>
              <tr>
                <th style={globalStyles.th}>Name</th>
                <th style={globalStyles.th}>Employee ID</th>
                <th style={globalStyles.th}>Email / Mobile</th>
                <th style={globalStyles.th}>Role</th>
                <th style={globalStyles.th}>Reporting Manager</th>
                <th style={globalStyles.th}>Region</th>
                <th style={globalStyles.th}>Certified</th>
                <th style={globalStyles.th}>Set Demo Person</th>
                <th style={globalStyles.th}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {users.length > 0 ? (
                users.map((user) => (
                  <tr key={getId(user)}>
                    <td style={globalStyles.td}>
                      <strong>{user.name || "—"}</strong>
                    </td>

                    <td style={globalStyles.td}>{user.employeeId || "—"}</td>

                    <td style={globalStyles.td}>
                      {user.email || "—"}
                      <br />
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>
                        {user.mobile || ""}
                      </span>
                    </td>

                    <td style={globalStyles.td}>
                      <span style={globalStyles.roleBadge(user.role)}>
                        {user.role || "—"}
                      </span>
                    </td>

                    <td style={globalStyles.td}>
                      {user.reportingManager ? (
                        <>
                          {user.reportingManager.name || "—"}
                          <br />
                          <small style={{ color: "#94a3b8" }}>
                            {user.reportingManager.employeeId || "—"}
                          </small>
                        </>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>—</span>
                      )}
                    </td>

                    <td style={globalStyles.td}>{user.region || "—"}</td>

                    <td style={globalStyles.td}>
                      <div style={globalStyles.toggleWrap}>
                        <button
                          type="button"
                          onClick={() => handleCertifiedToggle(user)}
                          disabled={!canEditUser(user)}
                          title={
                            user.isCertified
                              ? "Click to mark as not certified"
                              : "Click to mark as certified"
                          }
                          style={{
                            ...globalStyles.toggleBtn,
                            cursor: canEditUser(user)
                              ? "pointer"
                              : "not-allowed",
                            opacity: canEditUser(user) ? 1 : 0.45,
                          }}
                        >
                          {user.isCertified ? (
                            <MdOutlineToggleOn size={32} color="#10b981" />
                          ) : (
                            <MdOutlineToggleOff size={32} color="#ef4444" />
                          )}
                        </button>
                      </div>
                    </td>

                    <td style={globalStyles.td}>
                      <div style={globalStyles.toggleWrap}>
                        <button
                          type="button"
                          onClick={() => handleDemoPersonToggle(user)}
                          disabled={!canEditUser(user)}
                          title={
                            user.isDemoPerson
                              ? "Click to mark as not demo person"
                              : "Click to mark as demo person"
                          }
                          style={{
                            ...globalStyles.toggleBtn,
                            cursor: canEditUser(user)
                              ? "pointer"
                              : "not-allowed",
                            opacity: canEditUser(user) ? 1 : 0.45,
                          }}
                        >
                          {user.isDemoPerson ? (
                            <MdOutlineToggleOn size={32} color="#10b981" />
                          ) : (
                            <MdOutlineToggleOff size={32} color="#ef4444" />
                          )}
                        </button>
                      </div>
                    </td>

                    <td style={globalStyles.td}>
                      <div style={globalStyles.btnRow}>
                        <button
                          type="button"
                          title="View"
                          style={globalStyles.viewBtn}
                          onClick={() => handleView(user)}
                        >
                          <FiEye size={14} />
                        </button>

                        {canEditUser(user) && (
                          <button
                            type="button"
                            title="Edit"
                            style={globalStyles.editBtn}
                            onClick={() => openEdit(user)}
                          >
                            <FiEdit size={14} />
                          </button>
                        )}

                        {canDelete && (
                          <button
                            type="button"
                            title="Delete"
                            style={globalStyles.deleteBtn}
                            onClick={() => handleDelete(getId(user))}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    style={{ ...globalStyles.td, ...globalStyles.emptyState }}
                  >
                    No Users Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div style={globalStyles.paginateRow}>
          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: page === 1 ? 0.5 : 1,
            }}
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            {"<<"}
          </button>

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: page === 1 ? 0.5 : 1,
            }}
            disabled={page === 1}
            onClick={() => setPage((prev) => prev - 1)}
          >
            {"<"}
          </button>

          {getPageNumbers(page, totalPages).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              style={{
                ...globalStyles.paginateBtn,
                background:
                  page === pageNumber ? COLORS.primary : COLORS.background,
                color: page === pageNumber ? COLORS.white : COLORS.primary,
                minWidth: 36,
              }}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: isLastPage ? 0.5 : 1,
            }}
            disabled={isLastPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            {">"}
          </button>

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: isLastPage ? 0.5 : 1,
            }}
            disabled={isLastPage}
            onClick={() => setPage(totalPages)}
          >
            {">>"}
          </button>
        </div>
      )}

      {modal && (
        <div style={globalStyles.modal}>
          <div
            style={{
              ...globalStyles.mcard,
              ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
            }}
          >
            <h2 style={globalStyles.h2}>
              {editing ? "Edit User" : "Add User"}
            </h2>

            {err && <div style={globalStyles.errorText}>{err}</div>}

            <form onSubmit={handleSubmit}>
              <div
                style={{
                  ...globalStyles.srow,
                  ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                }}
              >
                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Name *</label>
                  <input
                    style={{
                      ...globalStyles.input,
                      ...(getFieldError("name") ? globalStyles.inputError : {}),
                    }}
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter name"
                  />
                  {getFieldError("name") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("name")}
                    </div>
                  )}
                </div>

                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Email *</label>
                  <input
                    style={{
                      ...globalStyles.input,
                      ...(getFieldError("email")
                        ? globalStyles.inputError
                        : {}),
                    }}
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter email"
                  />
                  {getFieldError("email") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("email")}
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  ...globalStyles.srow,
                  ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                }}
              >
                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Mobile *</label>
                  <input
                    style={{
                      ...globalStyles.input,
                      ...(getFieldError("mobile")
                        ? globalStyles.inputError
                        : {}),
                    }}
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter mobile"
                  />
                  {getFieldError("mobile") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("mobile")}
                    </div>
                  )}
                </div>

                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Role *</label>
                  <select
                    style={{
                      ...globalStyles.select,
                      ...(getFieldError("role") ? globalStyles.inputError : {}),
                    }}
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                  >
                    {allowedRoles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                  {getFieldError("role") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("role")}
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  ...globalStyles.srow,
                  ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                }}
              >
                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Region</label>
                  <input
                    style={globalStyles.input}
                    name="region"
                    value={form.region}
                    onChange={handleChange}
                    placeholder="Enter region"
                  />
                </div>

                {showReportingManagerDropdown && (
                  <div style={globalStyles.half}>
                    <label style={globalStyles.label}>Reporting Manager</label>
                    <select
                      style={globalStyles.select}
                      name="reportingManager"
                      value={form.reportingManager}
                      onChange={handleChange}
                    >
                      <option value="">Select Reporting Manager</option>

                      {reportingManagerOptions.map((manager) => (
                        <option key={getId(manager)} value={getId(manager)}>
                          {manager.name || "N/A"}
                          {manager.employeeId ? ` — ${manager.employeeId}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {showReportingManagerInfo && (
                  <div style={{ ...globalStyles.half, ...globalStyles.info }}>
                    This user will report to you.
                  </div>
                )}
              </div>

              <div
                style={{
                  ...globalStyles.srow,
                  ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                }}
              >
                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>
                    Password {editing ? "" : "*"}
                  </label>
                  <input
                    style={{
                      ...globalStyles.input,
                      ...(getFieldError("password")
                        ? globalStyles.inputError
                        : {}),
                    }}
                    type="text"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder={
                      editing
                        ? "Leave blank to keep old password"
                        : "Enter password"
                    }
                  />
                  {getFieldError("password") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("password")}
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  ...globalStyles.mrow,
                  ...(isMobile ? { justifyContent: "stretch" } : {}),
                }}
              >
                <button
                  type="button"
                  style={{
                    ...globalStyles.cancel,
                    ...(isMobile ? { width: "100%" } : {}),
                  }}
                  onClick={() => setModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{
                    ...globalStyles.btn,
                    ...(isMobile
                      ? { width: "100%", justifyContent: "center" }
                      : {}),
                  }}
                >
                  {editing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
